import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {useAgentInfo} from './use-agent-info'
import {isCopilotOrCustomAgent} from '../utils/agent-helpers'
import type {SelectedAgent} from '../types/session'

interface UseModelPickerEligibilityOptions {
  /** The currently selected agent — used for agent-type gating and agent-info fetch */
  selectedAgent?: SelectedAgent
  /** The agent's slug, if already known (skips useAgentInfo fetch) */
  agentSlug?: string
  /** When false, skips the agent-info fetch and returns ineligible immediately.
   *  Use this to pass a context-specific feature flag so the hook avoids
   *  unnecessary network requests when the UI won't render the picker. */
  enabled?: boolean
}

/**
 * Shared hook that determines whether the user is eligible to see a model picker
 * based on feature flags, plan/SKU, and agent type.
 */
export function useModelPickerEligibility({
  selectedAgent = {},
  agentSlug,
  enabled = true,
}: UseModelPickerEligibilityOptions = {}) {
  const {plan} = useEntitlement()

  const parsedAgentId = selectedAgent.topLevelAgentId ? Number(selectedAgent.topLevelAgentId) : NaN
  const agentIdForFetch =
    enabled && copilotFeatureFlags.codingAgentModelSelection && Number.isFinite(parsedAgentId) ? parsedAgentId : null
  const {data: agentInfo} = useAgentInfo(agentIdForFetch)

  const resolvedSlug = agentSlug ?? agentInfo?.slug

  const isEligible =
    enabled &&
    copilotFeatureFlags.codingAgentModelSelection &&
    (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
      plan === CopilotPlan.IndividualPro ||
      plan === CopilotPlan.IndividualProPlus ||
      plan === CopilotPlan.IndividualMax ||
      plan === CopilotPlan.IndividualStudent) &&
    isCopilotOrCustomAgent(selectedAgent, resolvedSlug)

  return {isEligible, agentInfo}
}
